import { createContext, useContext } from "react";
import { getFieldError } from "../utils";
/**
 * Wires a field's <input> to its error message via aria-errormessage
 * (ARIA 1.1). CalculationField provides the error element's id; input
 * components consume it. Both sides build the id via makeFieldErrorId()
 * so the string convention can't drift.
 *
 * Screen readers only announce the target when aria-invalid is also
 * true, so consumers should set both (see useAriaInvalidProps).
 *
 * See ./index.ts for the full decision tree on when to use which hook
 * vs. <AriaErrorScope> vs. a raw Provider reset.
 */
export function makeFieldErrorId(formTemplateFieldId) {
    return `${formTemplateFieldId}-error`;
}
export const AriaErrorIdContext = createContext(undefined);
export function useAriaErrorId() {
    return useContext(AriaErrorIdContext);
}
/**
 * Returns { aria-errormessage, aria-invalid } for spreading onto the
 * element that represents the field's invalid-able boundary (a MUI
 * FormControl, an input's inputProps, a role="group" wrapper). Values
 * are undefined when hasError is false, so React omits them.
 *
 * Note: MUI TextField already sets aria-invalid from its `error` prop.
 * In that case, use useAriaErrorId() directly to avoid setting
 * aria-invalid twice.
 */
export function useAriaInvalidProps(hasError) {
    const errorId = useAriaErrorId();
    return {
        "aria-errormessage": hasError && errorId ? errorId : undefined,
        "aria-invalid": hasError || undefined
    };
}
/**
 * Same as useAriaInvalidProps, but derives the error state from Formik
 * for composite fields that expose their error on an outer wrapper.
 * Callers still wrap children in <AriaErrorIdContext.Provider value={undefined}>
 * to stop the outer id from leaking onto nested inputs -- or use
 * <AriaErrorScope> which does both.
 */
export function useOuterFieldAriaInvalidProps(errors, touched, fieldName, errorDisplay) {
    const { error } = getFieldError(errors, touched, fieldName, errorDisplay);
    return useAriaInvalidProps(error);
}
//# sourceMappingURL=AriaErrorContext.js.map